import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { DashboardHeaderComponent } from '../structure/header/header';
import { DashboardSidebarComponent } from '../structure/sidebar/sidebar';
import { ServerTimeService } from '../../services/server-time';
import {
  DASHBOARD_EVENTS,
  DASHBOARD_STATS,
  DASHBOARD_SUMMARY,
  DEFAULT_DASHBOARD_USER,
  GENERATION_MIX,
  LOAD_TIMELINE,
} from './dashboard.data';
import {
  DashboardChartPoint,
  DashboardEvent,
  DashboardStatCard,
  DashboardSummaryCard,
  DashboardUserProfile,
} from './dashboard.interfaces';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements AfterViewInit, OnDestroy {
  @ViewChild('loadChartContainer') loadChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('mixChartContainer') mixChartContainer?: ElementRef<HTMLDivElement>;

  protected readonly user: DashboardUserProfile = DEFAULT_DASHBOARD_USER;
  protected readonly stats: DashboardStatCard[] = DASHBOARD_STATS;
  protected readonly summaryCards: DashboardSummaryCard[] = DASHBOARD_SUMMARY;
  protected readonly events: DashboardEvent[] = DASHBOARD_EVENTS;
  protected readonly loadTimeline: DashboardChartPoint[] = LOAD_TIMELINE;
  protected readonly generationMix = GENERATION_MIX;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;

  private loadChart?: Highcharts.Chart;
  private mixChart?: Highcharts.Chart;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly serverTimeSvc: ServerTimeService,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    queueMicrotask(() => {
      this.renderLoadChart();
      this.renderGenerationMixChart();
    });
  }

  ngOnDestroy(): void {
    this.loadChart?.destroy();
    this.mixChart?.destroy();
  }

  protected handleMenuToggle(): void {
    if (window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected get currentTimeLabel(): string {
    return this.serverTimeSvc.getCurrentTimeLabel();
  }

  private renderLoadChart(): void {
    if (!this.loadChartContainer) {
      return;
    }

    this.loadChart?.destroy();
    this.loadChart = Highcharts.chart(this.loadChartContainer.nativeElement, {
      chart: {
        type: 'areaspline',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Inter, Arial, sans-serif',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        itemStyle: { color: '#475569', fontWeight: '600' },
      },
      xAxis: {
        categories: this.loadTimeline.map((point) => point.label),
        lineColor: '#e2e8f0',
        tickColor: '#e2e8f0',
        labels: { style: { color: '#64748b' } },
      },
      yAxis: {
        title: { text: 'MW / Hz', style: { color: '#64748b' } },
        gridLineColor: '#eef2f7',
        labels: { style: { color: '#64748b' } },
      },
      tooltip: {
        shared: true,
        backgroundColor: '#0f172a',
        borderRadius: 12,
        style: { color: '#fff' },
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.18,
          lineWidth: 3,
          marker: {
            radius: 4,
            symbol: 'circle',
          },
        },
      },
      series: [
        {
          type: 'areaspline',
          name: 'Demand',
          color: '#2563eb',
          data: this.loadTimeline.map((point) => point.demand),
        },
        {
          type: 'areaspline',
          name: 'Generation',
          color: '#06b6d4',
          data: this.loadTimeline.map((point) => point.generation),
        },
      ],
    } as Highcharts.Options);
  }

  private renderGenerationMixChart(): void {
    if (!this.mixChartContainer) {
      return;
    }

    this.mixChart?.destroy();
    this.mixChart = Highcharts.chart(this.mixChartContainer.nativeElement, {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Inter, Arial, sans-serif',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories: this.generationMix.map((item) => item.name),
        lineColor: '#e2e8f0',
        tickColor: '#e2e8f0',
        labels: { style: { color: '#64748b' } },
      },
      yAxis: {
        title: { text: '%' },
        gridLineColor: '#eef2f7',
        labels: { style: { color: '#64748b' } },
      },
      tooltip: {
        backgroundColor: '#0f172a',
        borderRadius: 12,
        style: { color: '#fff' },
        pointFormat: '<b>{point.y}%</b>',
      },
      plotOptions: {
        column: {
          borderRadius: 8,
          pointPadding: 0.12,
          groupPadding: 0.1,
        },
      },
      series: [
        {
          type: 'column',
          name: 'Generation Mix',
          colorByPoint: true,
          data: this.generationMix.map((item) => item.value),
        },
      ],
    } as Highcharts.Options);
  }
}
